import { useTranslation } from 'react-i18next'

import { formatUsd } from '#/shared/lib/format'
import { Button } from '#/shared/ui/button'
import { ArrowRightIcon } from '#/shared/ui/icons'
import type { MarketRateRow } from '../model/types'
import { MarketRateStatusPill } from './market-rate-status-pill'
import { MarketRateTableSkeleton } from './market-rate-table-skeleton'

interface MarketRateTableProps {
  rows: MarketRateRow[]
  isLoading?: boolean
  onUseRate?: (row: MarketRateRow) => void
}

const COLUMN_WIDTHS = {
  vehicle: 'w-22',
  route: 'w-45',
  distance: 'w-22',
  listed: 'w-17',
  price: 'w-22',
  status: 'w-27',
  dispatched: 'w-22',
  actualPrice: 'w-25',
  action: 'w-30',
} as const

const EMPTY_VALUE = '—'

function PricePair({ price, perMi }: { price: number; perMi: number }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-0.5 leading-none">
      <span className="text-sm font-medium text-gray-900">
        {formatUsd(price)}
      </span>
      <span className="text-xs text-gray-500">
        ({t('table.marketRate.perMi', { rate: perMi.toFixed(2) })})
      </span>
    </div>
  )
}

function MutedText({ children }: { children: React.ReactNode }) {
  return <span className="text-xs text-gray-300">{children}</span>
}

function RouteCell({
  origin,
  destination,
}: {
  origin: string
  destination: string
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm font-medium text-gray-900">{origin}</span>
      <div className="flex items-center gap-1.25">
        <ArrowRightIcon className="h-2 w-2.5 text-gray-300" />
        <span className="text-sm text-gray-500">{destination}</span>
      </div>
    </div>
  )
}

function HeaderCell({
  label,
  width,
  align = 'start',
}: {
  label: string
  width: string
  align?: 'start' | 'center'
}) {
  return (
    <div
      className={`flex items-center px-3.5 ${width} ${
        align === 'center' ? 'justify-center' : ''
      }`}
    >
      <span className="text-2xs font-bold tracking-wide text-gray-300 uppercase">
        {label}
      </span>
    </div>
  )
}

function BodyCell({
  children,
  width,
  align = 'start',
}: {
  children: React.ReactNode
  width: string
  align?: 'start' | 'center'
}) {
  return (
    <div
      className={`flex items-center px-3.5 ${width} ${
        align === 'center' ? 'justify-center' : ''
      }`}
    >
      {children}
    </div>
  )
}

function TableHeader() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center border-b border-gray-200 bg-gray-100 py-2.5">
      <HeaderCell
        label={t('table.marketRate.columns.vehicle')}
        width={COLUMN_WIDTHS.vehicle}
      />
      <HeaderCell
        label={t('table.marketRate.columns.route')}
        width={COLUMN_WIDTHS.route}
      />
      <HeaderCell
        label={t('table.marketRate.columns.distance')}
        width={COLUMN_WIDTHS.distance}
      />
      <HeaderCell
        label={t('table.marketRate.columns.listed')}
        width={COLUMN_WIDTHS.listed}
      />
      <HeaderCell
        label={t('table.marketRate.columns.price')}
        width={COLUMN_WIDTHS.price}
      />
      <HeaderCell
        label={t('table.marketRate.columns.status')}
        width={COLUMN_WIDTHS.status}
      />
      <HeaderCell
        label={t('table.marketRate.columns.dispatched')}
        width={COLUMN_WIDTHS.dispatched}
      />
      <HeaderCell
        label={t('table.marketRate.columns.actualPrice')}
        width={COLUMN_WIDTHS.actualPrice}
      />
      <div className={COLUMN_WIDTHS.action} aria-hidden />
    </div>
  )
}

function TableRow({
  row,
  onUseRate,
}: {
  row: MarketRateRow
  onUseRate?: (row: MarketRateRow) => void
}) {
  const { t } = useTranslation()
  const ratePrice = row.actualPrice ?? row.listedPrice

  return (
    <div className="flex items-center border-b border-gray-200 bg-white py-2.5">
      <BodyCell width={COLUMN_WIDTHS.vehicle}>
        <span className="text-sm text-gray-900">{row.vehicleType}</span>
      </BodyCell>
      <BodyCell width={COLUMN_WIDTHS.route}>
        <RouteCell origin={row.origin} destination={row.destination} />
      </BodyCell>
      <BodyCell width={COLUMN_WIDTHS.distance}>
        {row.distanceMi !== undefined ? (
          <span className="text-sm text-gray-900">
            {t('table.marketRate.distance', { miles: row.distanceMi })}
          </span>
        ) : (
          <MutedText>{EMPTY_VALUE}</MutedText>
        )}
      </BodyCell>
      <BodyCell width={COLUMN_WIDTHS.listed}>
        {row.listedDate ? (
          <span className="text-xs text-gray-500">{row.listedDate}</span>
        ) : (
          <MutedText>{EMPTY_VALUE}</MutedText>
        )}
      </BodyCell>
      <BodyCell width={COLUMN_WIDTHS.price}>
        <PricePair price={row.listedPrice} perMi={row.listedPricePerMi} />
      </BodyCell>
      <BodyCell width={COLUMN_WIDTHS.status}>
        <MarketRateStatusPill
          status={row.status}
          fallbackLabel={row.statusLabel}
        />
      </BodyCell>
      <BodyCell width={COLUMN_WIDTHS.dispatched}>
        {row.dispatchedDate ? (
          <span className="text-xs text-gray-500">
            {t('table.marketRate.dispatchedOn', { date: row.dispatchedDate })}
          </span>
        ) : (
          <MutedText>{EMPTY_VALUE}</MutedText>
        )}
      </BodyCell>
      <BodyCell width={COLUMN_WIDTHS.actualPrice}>
        {row.actualPrice !== undefined && row.actualPricePerMi !== undefined ? (
          <PricePair price={row.actualPrice} perMi={row.actualPricePerMi} />
        ) : (
          <MutedText>{EMPTY_VALUE}</MutedText>
        )}
      </BodyCell>
      <BodyCell width={COLUMN_WIDTHS.action} align="center">
        <Button
          type="button"
          variant="outline"
          size="xxs"
          pressEffect
          onClick={() => onUseRate?.(row)}
        >
          {t('table.marketRate.useRate', { price: formatUsd(ratePrice) })}
        </Button>
      </BodyCell>
    </div>
  )
}

function TableBody({
  rows,
  isLoading,
  onUseRate,
}: {
  rows: MarketRateRow[]
  isLoading: boolean
  onUseRate?: (row: MarketRateRow) => void
}) {
  const { t } = useTranslation()

  if (isLoading) return <MarketRateTableSkeleton />
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center px-5 py-10">
        <span className="text-sm text-gray-500">
          {t('table.marketRate.empty')}
        </span>
      </div>
    )
  }
  return (
    <>
      {rows.map((row) => (
        <TableRow key={row.id} row={row} onUseRate={onUseRate} />
      ))}
    </>
  )
}

export function MarketRateTable({
  rows,
  isLoading = false,
  onUseRate,
}: MarketRateTableProps) {
  return (
    <div className="flex flex-col">
      <TableHeader />
      <TableBody rows={rows} isLoading={isLoading} onUseRate={onUseRate} />
    </div>
  )
}
